import { IconClockFilled } from '@iwoca/icons';
import { fetchGetPaymentInstructionOutcome } from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion-ui';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';

import Failed from './Failed/Failed';
import Loading from './Loading/Loading';
import Success from './Success/Success';
import { useStateKey } from '../../../iwQueryClient/useStateKey';

export const PaymentInitiated = ({
    paymentInstructionId,
    senderLogo,
    receiverLogo
}: {
    paymentInstructionId: string;
    senderLogo: string;
    receiverLogo: string;
}) => {
    const stateKey = useStateKey();

    const { data, isLoading, isError, refetch, isFetching } = useQuery({
        queryKey: [
            'fetchGetPaymentInstructionOutcome',
            paymentInstructionId,
            stateKey
        ],
        queryFn: async () =>
            await fetchGetPaymentInstructionOutcome({
                stateKey,
                paymentInstructionId
            }),
        select: (data) => data.data
    });

    if (isLoading)
        return <Loading senderLogo={senderLogo} receiverLogo={receiverLogo} />;

    if (isError)
        return <Failed senderLogo={senderLogo} receiverLogo={receiverLogo} />;

    switch (data.outcome) {
        case 'success':
            return (
                <Success senderLogo={senderLogo} receiverLogo={receiverLogo} />
            );
        case 'failure':
            return (
                <Failed senderLogo={senderLogo} receiverLogo={receiverLogo} />
            );
        case 'initiated':
        default:
            return (
                <div>
                    <div className="inline-block rounded-m bg-secondary-95 p-xl">
                        <IconClockFilled className="size-[40px]" />
                    </div>
                    <div className="text-center">
                        <h2>
                            <FormattedMessage
                                defaultMessage="Payment initiated"
                                id="_.processing_payment.payment_initiated.title"
                            />
                        </h2>
                        <p className="mx-auto max-w-[500px]">
                            <FormattedMessage
                                defaultMessage="Your payment is being processed. Please wait a few seconds, then click the button below to check the status of your payment."
                                id="_.processing_payment.payment_initiated.description"
                            />
                        </p>
                    </div>
                    <Button loading={isFetching} onClick={() => refetch()}>
                        <FormattedMessage
                            defaultMessage="Check payment status"
                            id="_.processing_payment.payment_initiated.cta"
                        />
                    </Button>
                </div>
            );
    }
};
